/**
 * TimeTracking — Registro de Tiempo e Inversión Horaria por Cliente.
 * 
 * - Widget de temporizador interactivo
 * - Horas acumuladas por cliente en el mes
 * - Registro manual de horas trabajadas
 * - Historial detallado de sesiones
 */

import { useEffect, useState } from 'react';
import {
  Timer,
  Play,
  Pause,
  Square,
  Clock,
  Plus,
  Loader2,
  X,
  Hourglass,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { formatDuration, formatShortDate } from '@/lib/utils';
import { useTimerStore, getTotalElapsed } from '@/stores/timerStore';
import { useAppStore } from '@/stores/appStore';
import type { Client, TimeEntry } from '@/types';

export function TimeTracking() {
  const { currentPeriod, setCurrentPeriod } = useAppStore();
  const timer = useTimerStore();
  const [clients, setClients] = useState<Client[]>([]);
  const [entries, setEntries] = useState<(TimeEntry & { client_name?: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [, setTick] = useState(0);

  // Selector para iniciar timer
  const [timerClientId, setTimerClientId] = useState('');

  // Modal para registro manual
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualClientId, setManualClientId] = useState('');
  const [manualHours, setManualHours] = useState('1');
  const [manualMinutes, setManualMinutes] = useState('0');
  const [manualDate, setManualDate] = useState(new Date().toISOString().slice(0, 10));
  const [manualDescription, setManualDescription] = useState('');
  const [submittingManual, setSubmittingManual] = useState(false);

  // Tick para el timer activo
  useEffect(() => {
    if (!timer.isRunning) return;
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [timer.isRunning]);

  // Carga de clientes y registros de tiempo
  const loadTimeData = async () => {
    setLoading(true);
    try {
      const cliRes = await api.getClients();
      const clientList = cliRes.clients as Client[];
      setClients(clientList);

      // Cargar entradas de todos los clientes para el mes actual
      const entryPromises = clientList.map(async (c) => {
        try {
          const res = await api.getTimeEntries(c.id, currentPeriod);
          return (res.timeEntries || []).map((e: TimeEntry) => ({
            ...e,
            client_name: c.name,
          }));
        } catch {
          return [];
        }
      });

      const nested = await Promise.all(entryPromises);
      const allEntries = nested.flat().sort((a, b) => 
        new Date(b.start_time).getTime() - new Date(a.start_time).getTime()
      );
      setEntries(allEntries);
    } catch (err) {
      console.error('Error cargando tiempo:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimeData();
  }, [currentPeriod]);

  // Cálculo de totales
  const totalSecondsMonth = entries.reduce((acc, curr) => acc + (curr.duration_seconds || 0), 0);

  // Horas por cliente
  const clientBreakdown = clients.map((c) => {
    const clientSecs = entries
      .filter((e) => e.client_id === c.id)
      .reduce((acc, curr) => acc + (curr.duration_seconds || 0), 0);
    const percentage = totalSecondsMonth > 0 ? (clientSecs / totalSecondsMonth) * 100 : 0;
    return {
      client: c,
      seconds: clientSecs,
      percentage: Math.round(percentage),
    };
  }).filter(item => item.seconds > 0 || item.client.status === 'active');

  const elapsed = getTotalElapsed(timer);
  const isTimerActive = timer.isRunning || timer.elapsed > 0;

  const handleStartTimer = () => {
    const client = clients.find((c) => c.id === timerClientId);
    if (client) {
      timer.start(client.id, client.name);
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualClientId) return;

    const totalSeconds = (parseInt(manualHours || '0', 10) * 3600) + (parseInt(manualMinutes || '0', 10) * 60);
    if (totalSeconds <= 0) return;

    setSubmittingManual(true);
    try {
      const startTime = new Date(manualDate);
      const endTime = new Date(startTime.getTime() + totalSeconds * 1000);

      await api.createTimeEntry(manualClientId, {
        description: manualDescription || 'Registro manual de tiempo',
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        duration_seconds: totalSeconds,
      });

      setShowManualModal(false);
      setManualDescription('');
      setManualHours('1');
      setManualMinutes('0');
      // Recargar datos
      await loadTimeData();
    } catch (err) {
      console.error('Error guardando tiempo manual:', err);
    } finally {
      setSubmittingManual(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ─── Header ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Timer className="text-primary" />
            Control de Tiempo
          </h1>
          <p className="text-muted-foreground text-sm">
            Monitorea las horas invertidas por cliente y optimiza tu rentabilidad por hora.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="month"
            value={currentPeriod}
            onChange={(e) => setCurrentPeriod(e.target.value)}
            className="rounded-md border bg-background px-3 py-1.5 text-sm font-medium"
          />

          <Button onClick={() => setShowManualModal(true)} variant="outline" className="gap-2">
            <Plus size={16} />
            Cargar Horas Manual
          </Button>
        </div>
      </div>

      {/* ─── Fila Superior: Timer Activo & Total Mensual ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tarjeta del Timer en Vivo */}
        <Card className={`lg:col-span-2 ${timer.isRunning ? 'border-primary shadow-lg shadow-primary/10' : ''}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className={timer.isRunning ? 'text-primary animate-pulse' : 'text-muted-foreground'} size={20} />
              Temporizador de Trabajo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-muted/40 border">
              <div>
                <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1">
                  {timer.clientName ? `Cliente: ${timer.clientName}` : 'Selecciona un cliente para iniciar'}
                </span>
                <p className="text-5xl font-mono font-black tracking-widest text-foreground">
                  {String(Math.floor(elapsed / 3600)).padStart(2, '0')}:
                  {String(Math.floor((elapsed % 3600) / 60)).padStart(2, '0')}:
                  {String(elapsed % 60).padStart(2, '0')}
                </p>
              </div>

              {/* Botones de acción */}
              <div className="flex flex-wrap items-center gap-2">
                {!isTimerActive ? (
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <select
                      value={timerClientId}
                      onChange={(e) => setTimerClientId(e.target.value)}
                      className="rounded-md border bg-background px-3 py-2 text-sm"
                    >
                      <option value="">Seleccionar cliente...</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>

                    <Button onClick={handleStartTimer} disabled={!timerClientId} className="gap-2">
                      <Play size={16} /> Iniciar
                    </Button>
                  </div>
                ) : (
                  <>
                    {timer.isRunning ? (
                      <Button variant="outline" onClick={timer.pause} className="gap-2">
                        <Pause size={16} /> Pausar
                      </Button>
                    ) : (
                      <Button onClick={timer.resume} className="gap-2">
                        <Play size={16} /> Continuar
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      onClick={async () => {
                        await timer.stop();
                        await loadTimeData();
                      }}
                      className="gap-2"
                    >
                      <Square size={16} /> Guardar y Detener
                    </Button>
                  </>
                )}
              </div>
            </div>

            {isTimerActive && (
              <input
                type="text"
                placeholder="¿Qué estás haciendo? (Ej: Editando Reels, Planificando copy...)"
                value={timer.description}
                onChange={(e) => timer.setDescription(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            )}
          </CardContent>
        </Card>

        {/* Resumen Total del Mes */}
        <Card className="flex flex-col justify-between">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Hourglass size={18} className="text-primary" />
              Total Invertido en {currentPeriod}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-4xl font-extrabold text-foreground">
                {(totalSecondsMonth / 3600).toFixed(1)} <span className="text-xl font-normal text-muted-foreground">horas</span>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {entries.length} sesiones de trabajo registradas
              </p>
            </div>

            <div className="rounded-lg bg-primary/5 p-3 text-xs text-muted-foreground">
              💡 Tip: Mantener el registro de tiempo te permite calcular tu tarifa horaria real y saber qué clientes demandan más dedicación.
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ─── Desglose por Cliente (Barras de progreso) ─── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Distribución por Cliente ({currentPeriod})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {clientBreakdown.map((item) => (
              <div key={item.client.id} className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold flex items-center gap-2">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: item.client.avatar_color || '#6366f1' }}
                    />
                    {item.client.name}
                  </span>
                  <span className="text-muted-foreground font-mono">
                    {(item.seconds / 3600).toFixed(1)} h ({item.percentage}%)
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${item.percentage}%`,
                      backgroundColor: item.client.avatar_color || '#6366f1',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ─── Tabla de Sesiones Recientes ─── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historial de Sesiones</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Timer className="mx-auto h-12 w-12 text-muted-foreground/30 mb-2" />
              <p>No hay tiempo registrado en {currentPeriod}.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="pb-3 font-semibold">Cliente</th>
                    <th className="pb-3 font-semibold">Descripción / Tarea</th>
                    <th className="pb-3 font-semibold">Fecha</th>
                    <th className="pb-3 font-semibold text-right">Duración</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-muted/30 transition-colors">
                      <td className="py-3 font-semibold text-foreground">
                        {entry.client_name || '—'}
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {entry.description || entry.task_title || 'Sesión de trabajo'}
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {formatShortDate(entry.start_time)}
                      </td>
                      <td className="py-3 text-right font-mono font-medium text-foreground">
                        {formatDuration(entry.duration_seconds)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Modal Carga Manual ─── */}
      {showManualModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-xl border animate-in">
            <div className="flex items-center justify-between pb-3 border-b">
              <h2 className="text-lg font-bold">Cargar Tiempo Manual</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowManualModal(false)}
                className="h-8 w-8"
              >
                <X size={16} />
              </Button>
            </div>

            <form onSubmit={handleManualSubmit} className="mt-4 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Cliente
                </label>
                <select
                  required
                  value={manualClientId}
                  onChange={(e) => setManualClientId(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">Seleccionar cliente...</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Horas
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="24"
                    required
                    value={manualHours}
                    onChange={(e) => setManualHours(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Minutos
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    required
                    value={manualMinutes}
                    onChange={(e) => setManualMinutes(e.target.value)}
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Fecha de trabajo
                </label>
                <input
                  type="date"
                  required
                  value={manualDate}
                  onChange={(e) => setManualDate(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Descripción del trabajo realizado
                </label>
                <textarea
                  rows={2}
                  placeholder="Ej: Sesión fotográfica presencial y selección de tomas..."
                  value={manualDescription}
                  onChange={(e) => setManualDescription(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowManualModal(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={submittingManual}>
                  {submittingManual ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : null}
                  Guardar Tiempo
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
